import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import SelectComponent from "../../components/SelectComponent/SelectComponent";
import lang from "../../assets/lang.png";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import {
  updateCss,
  updateDefaultLanguage,
  updateLanguageData,
} from "../../store/slice/widgetSetting.slice";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import calenderImg from "../../assets/salon-calender.png";
import DateComponent from "./DateComponent";
import TimeComponent from "./TimeComponent";
import GenderComponent from "./GenderComponent";
import TreatmentType from "./TreatmentType";
import StylishComponent from "./StylishComponent";
import {
  fetchAdditionalFields,
  fetchAvailabilityDates,
  fetchAvailabilityTime,
  fetchGenderType,
  fetchGuestDetails,
  fetchStylist,
  fetchTreatment,
  fetchUpsaleProducts,
  fetchWidgetSettings,
  getLanguageDetails,
} from "../../api/SalonService";
import {
  setAvailabilityDate,
  setAvailabilityTime,
  setFieldsLoader,
  setGenderType,
  setGetFieldsData,
  setGuestDetails,
  setGuestLoader,
  setLanguageWords,
  setProffesionalDetails,
  setTotalPrice,
  setTreatmentType,
  setUpsaleLoader,
  setUpsaleProducts,
  setWidgetSettings,
  updateButtonText,
  setLangLoader,
  setWidgetLoadet,
} from "../../store/slice/Salon.slice";
import CloseIcon from "@mui/icons-material/Close";
import { format_Number } from "../../utils/utils";
import { WidgetHeightProvider, useWidgetHeight } from "../horecaWidget/widgetHeightContext";

dayjs?.extend(customParseFormat);
const WidgetContent = ({ widgetSetupId }) => {
  const theme = useTheme();
  const { rootRef, onPopoverOpen, onPopoverClose } = useWidgetHeight();

  const { key, resType, languageData, defaultLanguage, widgetConfig, css } =
    useSelector((store) => store?.widgetSettings);
  const bookbtnCss = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Book button"
  )?.fields;

  const IMG_URL = process.env.REACT_APP_API_LOCAL_IMG_URL;

  const {
    genderType,
    selectedTreatmentType,
    selectedGenderType,
    selectedProffesionalDetails,
    selectedDate,
    selectedTime,
    totalPrice,
    languageWords,
    widgetSettings,
    langLoader,
    widgetLoader,
  } = useSelector((store) => store?.salon);

  const hasImage      = Boolean(widgetSettings?.settings?.imageFileName?.trim());
  const widgetPosition = widgetSettings?.settings?.widgetPosition ?? "Top";

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const location      = useLocation();
  const queryParams   = new URLSearchParams(location.search);
  const navigate      = useNavigate();
  const dispatch      = useDispatch();

  // ── refs (anchors for popovers) ───────────────────────────────────────────
  const genderRef    = useRef(null);
  const treatmentRef = useRef(null);
  const stylishRef   = useRef(null);
  const calenderRef  = useRef(null);
  const timeRef      = useRef(null);

  // ── popover open/close state ──────────────────────────────────────────────
  const [isGender,        setIsGenderOpen]    = useState(false);
  const [isTreatment,     setIsTreatmentOpen] = useState(false);
  const [isTimeComponent, setIsTimeComponent] = useState(false);
  const [isDate,          setIsDateOpen]      = useState(false);
  const [isStylish,       setIsStylishOpen]   = useState(false);
  const [openWidget,      setOpenWidget]      = useState(
    location?.state?.openWidgetScreen ?? false
  );

  // ── data loaders ──────────────────────────────────────────────────────────
  const [genderLoader,    setGenderLoader]    = useState(false);
  const [treatmentLoader, setTreatmentLoader] = useState(false);
  const [stylistLoader,   setStylistLoader]   = useState(false);
  const [dateLoader,      setDateLoader]      = useState(false);
  const [timeLoader,      setTimeLoader]      = useState(false);

  // ── close-all helper (prevents two popovers open at once on mobile) ───────
  const closeAll = useCallback((except) => {
    if (except !== "gender")    setIsGenderOpen(false);
    if (except !== "treatment") setIsTreatmentOpen(false);
    if (except !== "stylish")   setIsStylishOpen(false);
    if (except !== "date")      setIsDateOpen(false);
    if (except !== "time")      setIsTimeComponent(false);
  }, []);

  const toggle = (setter, key, isCurrentlyOpen) => () => {
    if (isCurrentlyOpen) {
      setter(false);
      onPopoverClose();
    } else {
      closeAll(key);
      setter(true);
      onPopoverOpen();
    }
  };

  const close = (setter) => () => {
    setter(false);
    onPopoverClose();
  };

  // ── language ──────────────────────────────────────────────────────────────
  const handleLanguageChange = (event) => {
    const selectedLangCode = event.target.value;
    const selectedLanguage = languageData.find(
      (item) => item.langcode === selectedLangCode
    );
    dispatch(updateDefaultLanguage(selectedLanguage?.langcode));
  };

  // ── API calls ─────────────────────────────────────────────────────────────
  const getWidgetDetails = async (key, lang, id) => {
    dispatch(setWidgetLoadet(true));
    try {
      const response = await fetchWidgetSettings(key, lang, id);
      const result = response?.data?.basicLanguages?.reduce((acc, item) => {
        const tag  = item.tag.replace(/{|}/g, "").replace(/\s+/g, "");
        const data = item.widgetTranslateLanguages[0]?.name;
        acc[tag]   = data;
        return acc;
      }, {});
      dispatch(updateCss(response?.data?.settings?.cssGroup));
      dispatch(setWidgetSettings(response?.data));
      dispatch(
        updateButtonText(
          response?.data?.settings?.buttonText ||
          response?.data?.fields?.reservationButtonText
        )
      );
      dispatch(setLanguageWords(result));
      dispatch(setWidgetLoadet(false));
    } catch (error) {
      console.error("error:", error?.message);
      dispatch(setWidgetLoadet(false));
    }
  };

  const getGenderDetails = async () => {
    try {
      setGenderLoader(true);
      const response = await fetchGenderType(key, defaultLanguage);
      dispatch(setGenderType(response?.data));
      setGenderLoader(false);
    } catch (error) {
      console.error("error:", error?.message);
      setGenderLoader(false);
    }
  };

  const getTreatmentDetails = async () => {
    setTreatmentLoader(true);
    try {
      const response = await fetchTreatment(
        key,
        defaultLanguage,
        selectedGenderType?.idGender,
        Number(widgetSetupId)
      );
      dispatch(setTreatmentType(response?.data));
      setTreatmentLoader(false);
    } catch (error) {
      console.error("error:", error?.message);
      setTreatmentLoader(false);
    }
  };

  const fetchLanguageDetails = async () => {
    dispatch(setLangLoader(true));
    try {
      const response = await getLanguageDetails(key);
      if (!response || response.status !== 200) return;

      const langParamFromURL = queryParams.get("lang");
      const isValidLang =
        langParamFromURL &&
        !["null", "undefined", ""].includes(langParamFromURL.trim());
      let selectedLang   = isValidLang ? langParamFromURL : defaultLanguage;
      const languageList = response?.data || [];

      if (!selectedLang) {
        selectedLang = languageList.find((item) => item?.defaultLanguage)?.langcode;
      }
      if (selectedLang) dispatch(updateDefaultLanguage(selectedLang));
      dispatch(updateLanguageData(languageList));
      localStorage.setItem(
        "widget",
        JSON.stringify({ id: widgetSetupId, key, lang: selectedLang, resType })
      );
      dispatch(setLangLoader(false));
    } catch (error) {
      console.error("Error fetching language details widget:", error?.message);
      dispatch(setLangLoader(false));
    }
  };

  const getStylistDetails = async () => {
    setStylistLoader(true);
    try {
      const response = await fetchStylist(
        key, defaultLanguage, selectedTreatmentType?.idSalonTreatment
      );
      dispatch(setProffesionalDetails(response?.data));
      setStylistLoader(false);
    } catch (error) {
      console.error("error:", error?.message);
      setStylistLoader(false);
    }
  };

  const getAvailbalityDate = async () => {
    setDateLoader(true);
    const idProffesional =
      typeof selectedProffesionalDetails === "string"
        ? 0
        : selectedProffesionalDetails?.idSalonProfessional;
    const isNoPrefrence = typeof selectedProffesionalDetails === "string";
    try {
      const response = await fetchAvailabilityDates(
        key, idProffesional, selectedTreatmentType?.idSalonTreatment, isNoPrefrence
      );
      dispatch(setAvailabilityDate(response?.data));
      setDateLoader(false);
    } catch (error) {
      console.error("error:", error?.message);
      setDateLoader(false);
    }
  };

  const getAvailbalityTime = async () => {
    setTimeLoader(true);
    const idProffesional =
      typeof selectedProffesionalDetails === "string"
        ? 0
        : selectedProffesionalDetails?.idSalonProfessional;
    const isNoPrefrence = typeof selectedProffesionalDetails === "string";
    try {
      const response = await fetchAvailabilityTime(
        key,
        idProffesional,
        selectedTreatmentType?.idSalonTreatment,
        dayjs(selectedDate, "DD-MM-YYYY").format("YYYY-MM-DD"),
        isNoPrefrence
      );
      dispatch(setAvailabilityTime(response?.data));
      setTimeLoader(false);
    } catch (error) {
      console.error("error:", error?.message);
      setTimeLoader(false);
    }
  };

  const getUpsaleProducts = async () => {
    dispatch(setUpsaleLoader(true));
    try {
      const response = await fetchUpsaleProducts(
        key, selectedTreatmentType?.idSalonTreatment
      );
      dispatch(setUpsaleProducts(response?.data));
      dispatch(setUpsaleLoader(false));
    } catch (error) {
      console.error("error:", error?.message);
      dispatch(setUpsaleLoader(false));
    }
  };

  const getGuestDetails = async () => {
    dispatch(setGuestLoader(true));
    try {
      const response = await fetchGuestDetails(key, defaultLanguage);
      dispatch(setGuestDetails(response?.data));
      dispatch(setGuestLoader(false));
    } catch (error) {
      dispatch(setGuestLoader(false));
    }
  };

  const getExtraFieldsDetails = async () => {
    dispatch(setFieldsLoader(true));
    try {
      const response = await fetchAdditionalFields(
        selectedTreatmentType?.idSalonTreatment, defaultLanguage, key
      );
      dispatch(setGetFieldsData(response?.data));
      dispatch(setFieldsLoader(false));
    } catch (error) {
      dispatch(setFieldsLoader(false));
    }
  };

  // ── effects: data fetching ────────────────────────────────────────────────
  useEffect(() => {
    if (key && defaultLanguage) {
      getGenderDetails();
      getGuestDetails();
      getWidgetDetails(key, defaultLanguage, widgetSetupId);
    }
  }, [defaultLanguage]);

  useEffect(() => {
    if (key) fetchLanguageDetails();
  }, [key]);

  useEffect(() => {
    if (key && defaultLanguage && Object.keys(selectedGenderType)?.length > 0)
      getTreatmentDetails();
  }, [selectedGenderType]);

  useEffect(() => {
    if (Object.keys(selectedTreatmentType)?.length > 0) {
      getStylistDetails();
      getUpsaleProducts();
      getExtraFieldsDetails();
      if (selectedTreatmentType?.price > 0)
        dispatch(setTotalPrice(selectedTreatmentType?.price));
    }
  }, [selectedTreatmentType]);

  useEffect(() => {
    if (
      (typeof selectedProffesionalDetails === "string" ||
        selectedProffesionalDetails?.firstName) &&
      selectedTreatmentType?.idSalonTreatment
    ) {
      getAvailbalityDate();
    }
  }, [selectedProffesionalDetails, selectedTreatmentType?.idSalonTreatment]);

  useEffect(() => {
    if (selectedDate) getAvailbalityTime();
  }, [selectedDate]);

  // ── derived values ────────────────────────────────────────────────────────
  const professionalName =
    typeof selectedProffesionalDetails === "string"
      ? selectedProffesionalDetails
      : selectedProffesionalDetails?.firstName
      ? `${selectedProffesionalDetails.firstName} ${
          selectedProffesionalDetails.lastName || ""
        }`.trim()
      : "";

  // ── loading screen ────────────────────────────────────────────────────────
  if (langLoader || widgetLoader) {
    return (
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={langLoader || widgetLoader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box
      ref={rootRef}           // ← WidgetHeightProvider measures this element
      sx={{
        position: "relative",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        overflowX: "hidden",  // prevent horizontal scroll from open popovers
        ...(hasImage
          ? {
              backgroundImage: `url(${IMG_URL}/upload/Widgetsetup${widgetSettings?.settings?.imageFileName.replace(
                /\\/g,
                "/"
              )})`,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              minHeight: widgetSettings?.settings?.imageHeight
                ? `${widgetSettings.settings.imageHeight}px`
                : "100vh",
            }
          : { backgroundColor: theme.widget?.mainBg }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: isSmallScreen ? "flex-start" : "flex-end",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1200px",
            padding: { xs: 2, sm: 3 },
            position: "absolute",
            ...(!hasImage && { top: 0 }),
            ...(hasImage && widgetPosition === "Top"    && { top: 0 }),
            ...(hasImage && widgetPosition === "Bottom" && { bottom: 0 }),
            ...(hasImage && widgetPosition === "Middle" && {
              top: "50%",
              transform: "translateY(-50%)",
            }),
          }}
        >
          {/* ── Language selector ── */}
          {languageData?.length > 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
                mb: 2,
              }}
            >
              <FormControl
                variant="outlined"
                sx={{
                  minWidth: 120,
                  backgroundColor: theme?.widget?.langBgColor || "#fff",
                  width: { xs: "100px", md: "120px", sm: "100px" },
                  height: "45px",
                  borderRadius: "5px",
                }}
              >
                <Select
                  value={defaultLanguage}
                  onChange={handleLanguageChange}
                  MenuProps={{
                    disablePortal: true,
                    disableEnforceFocus: true,
                    disableAutoFocus: true,
                    disableRestoreFocus: true,
                    autoFocus: false,
                  }}
                  displayEmpty
                  renderValue={() => (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <img
                        src={lang}
                        alt="lang-img"
                        style={{ width: "40px", marginRight: "10px" }}
                      />
                    </Box>
                  )}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline":        { borderColor: "transparent" },
                    "&:hover .MuiOutlinedInput-notchedOutline":  { borderColor: "transparent" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                    "& .MuiOutlinedInput-input": { padding: 1, fontSize: "12px" },
                  }}
                >
                  {languageData?.map((item) => (
                    <MenuItem
                      key={item?.idlanguage}
                      value={item?.langcode}
                      sx={{
                        color: "grey",
                        "&.Mui-selected": { backgroundColor: "#A57939 !important", color: "#fff" },
                        "&:hover": {
                          backgroundColor: "#FADA7A",
                          color: "grey",
                          "&.Mui-selected": { backgroundColor: "#FADA7A", color: "grey" },
                        },
                      }}
                    >
                      {item?.langcode === defaultLanguage ? (
                        <>
                          {item?.language}
                          <CheckBoxIcon sx={{ color: "#fff", marginRight: "8px", ml: 2 }} />
                        </>
                      ) : (
                        item?.language
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {widgetConfig?.settings?.floatButtonActive &&
                widgetConfig?.settings?.buttonText &&
                openWidget && (
                  <CloseIcon
                    sx={{
                      color: "lightgrey",
                      "&:hover": { color: theme?.widgetButton?.textcloseTextHover },
                      cursor: "pointer",
                    }}
                    onClick={() => setOpenWidget(!openWidget)}
                  />
                )}
            </Box>
          )}

          {/* ── Widget row ── */}
          <Box>
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: "1.2rem",
                    color: theme?.widget?.titleTextClr,
                    textAlign: "left",
                    mb: 1,
                  }}
                >
                  {languageWords?.widgetHeading || "Start uw reservering"}
                </Typography>
              </Grid>
            </Grid>

            {/*
              position: "relative" establishes a stacking context so that
              absolutely-positioned popover children don't escape this layer.
            */}
            <Grid
              container
              spacing={2}
              sx={{ mb: 3, position: "relative" }}
              alignItems="center"
            >
              {/* Gender */}
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <SelectComponent
                  label={languageWords?.Gender || "Gender"}
                  value={selectedGenderType?.gender}
                  onClick={toggle(setIsGenderOpen, "gender", isGender)}
                  ref={genderRef}
                  disabled={genderType?.length === 0}
                  sx={{
                    width: "100%",
                    height: "72px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: `1px solid ${theme?.widget?.inputBorderColor}`,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: `1px solid ${theme?.widget?.inputBorderColor}`,
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: 1,
                      color: theme?.guest?.textColor,
                      ml: 3,
                      mt: 1,
                      fontSize: { sm: "12px", md: "14px" },
                    },
                  }}
                />
                <GenderComponent
                  isOpen={isGender}
                  handleClose={close(setIsGenderOpen)}
                  Ref={genderRef}
                  loader={genderLoader}
                />
              </Grid>

              {/* Treatment */}
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <SelectComponent
                  label={languageWords?.Treatment || "Treatment"}
                  value={selectedTreatmentType?.name}
                  onClick={toggle(setIsTreatmentOpen, "treatment", isTreatment)}
                  ref={treatmentRef}
                  disabled={Object.keys(selectedGenderType).length === 0}
                />
                <TreatmentType
                  isOpen={isTreatment}
                  handleClose={close(setIsTreatmentOpen)}
                  Ref={treatmentRef}
                  loader={treatmentLoader}
                />
              </Grid>

              {/* Stylist */}
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <SelectComponent
                  label={languageWords?.Stylist || "Stylist"}
                  value={professionalName}
                  onClick={toggle(setIsStylishOpen, "stylish", isStylish)}
                  ref={stylishRef}
                  disabled={Object.keys(selectedTreatmentType).length === 0}
                />
                <StylishComponent
                  isOpen={isStylish}
                  handleClose={close(setIsStylishOpen)}
                  Ref={stylishRef}
                  loader={stylistLoader}
                />
              </Grid>

              {/* Date */}
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <SelectComponent
                  label={languageWords?.date}
                  value={selectedDate}
                  onClick={toggle(setIsDateOpen, "date", isDate)}
                  ref={calenderRef}
                  icon={calenderImg}
                  isIcon={true}
                  disabled={
                    (typeof selectedProffesionalDetails === "object" &&
                      !selectedProffesionalDetails?.firstName) ||
                    (typeof selectedProffesionalDetails === "string" &&
                      !selectedProffesionalDetails)
                  }
                />
                <DateComponent
                  isOpen={isDate}
                  handleClose={close(setIsDateOpen)}
                  Ref={calenderRef}
                  loader={dateLoader}
                />
              </Grid>

              {/* Time */}
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <SelectComponent
                  label={languageWords?.Time}
                  value={selectedTime?.timeRange}
                  onClick={toggle(setIsTimeComponent, "time", isTimeComponent)}
                  ref={timeRef}
                  disabled={!selectedDate}
                />
                <TimeComponent
                  isOpen={isTimeComponent}
                  handleClose={close(setIsTimeComponent)}
                  Ref={timeRef}
                  loader={timeLoader}
                />
              </Grid>

              {/* Book Now */}
              <Grid item xs={12} sm={6} md={4} lg={2}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: bookbtnCss?.salonBtnBgClr,
                    color: bookbtnCss?.salonBtnTxtClr,
                    height: "70px",
                    fontWeight: "bold",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    "&.Mui-disabled": {
                      backgroundColor: bookbtnCss?.salonBtndisabled,
                      color: bookbtnCss?.salonBtnDisabledTxt,
                    },
                  }}
                  onClick={() => navigate("/bookSalon")}
                  disabled={
                    Object.keys(selectedGenderType)?.length === 0 ||
                    Object.keys(selectedTreatmentType)?.length === 0 ||
                    Object.keys(selectedProffesionalDetails)?.length === 0 ||
                    !selectedDate ||
                    !selectedTime
                  }
                >
                  {languageWords?.Booknow}
                  <Box
                    component="span"
                    sx={{
                      color: theme?.widget?.bookNowBtnPriceText ?? "#fff",
                      fontSize: theme?.widget?.bookNowBtnPriceFontSize ?? "13px",
                      fontWeight: theme?.widget?.bookNowBtnPriceFontWeight ?? 200,
                    }}
                  >
                    {totalPrice > 0
                      ? `€${format_Number(parseFloat(totalPrice).toFixed(2))}`
                      : ""}
                  </Box>
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
const Widget = ({ widgetSetupId, heightChange }) => {
  const { widgetSettings } = useSelector((store) => store?.salon);
  const hasImage = Boolean(widgetSettings?.settings?.imageFileName?.trim());

  return (
    <WidgetHeightProvider onHeightChange={heightChange} disabled={hasImage}>
      <WidgetContent widgetSetupId={widgetSetupId} />
    </WidgetHeightProvider>
  );
};

export default Widget;
